/**
 * Single source of truth for "what colour is this status".
 *
 * Extracted verbatim from the switch that used to live inside
 * `components/RFQ/StatusBadge/StatusBadge.js`, which now consumes this too. The
 * card's top border and the status badge have to agree, and the only way to
 * guarantee that is for both to read the same map — a second, hand-copied table
 * would drift the first time a status is added.
 *
 * NOTE the token names are historically inverted and are preserved as-is rather
 * than renamed (they are load-bearing in `Badge`'s `colors` map): `active` is
 * GREEN and `success` is BLUE.
 */
export type RfxStatusColour = "active" | "default" | "error" | "success";

export interface RfxStatusPresentation {
	colour: RfxStatusColour;
	text: string;
}

/**
 * @param status      the status being rendered (RFx status, or an invitee's).
 * @param isDraftRfx  whether the PARENT RFx is a draft — an `INVITED` supplier
 *                    on an unpublished draft is only "Prequalified", not yet
 *                    actually invited.
 */
export const getRfxStatusPresentation = (
	status: string | null | undefined,
	isDraftRfx = false
): RfxStatusPresentation => {
	switch (status?.toUpperCase()) {
		case "ACTIVE":
			return { colour: "active", text: "Open" };
		case "COMPLETE":
			return { colour: "success", text: "Complete" };
		case "ON_HOLD":
			return { colour: "default", text: "On Hold" };
		case "ARCHIVED":
			return { colour: "default", text: "Archived" };
		case "EXPIRED":
			return { colour: "error", text: "Expired" };
		case "CANCELLED":
			return { colour: "error", text: "Cancelled" };
		case "SUSPENDED":
			return { colour: "error", text: "Suspended" };
		case "PREQUALIFIED":
			// Draft RFx with no participants saved yet — not a backend status.
			return { colour: "default", text: "Prequalified" };
		case "INVITED":
			return isDraftRfx
				? { colour: "default", text: "Prequalified" }
				: { colour: "success", text: "Invited" };
		case "SUBMITTED":
			return { colour: "active", text: "Submitted " };
		case "ACCEPTED":
			return { colour: "success", text: "Accepted" };
		case "EVALUATION":
			return { colour: "success", text: "Evaluation" };
		case "AWARDED":
			return { colour: "active", text: "Awarded" };
		case "DECLINE":
			return { colour: "error", text: "Declined" };
		case "INPROGRESS":
			return { colour: "success", text: "In Progress" };
		case "BEING_REVISED":
			return { colour: "active", text: "Being Revised" };
		default:
			return { colour: "default", text: status ?? "" };
	}
};

/**
 * Card top-border class per status colour. These are the same `-700` shades the
 * badge uses for its text and dot, so a card's border reads as the same colour
 * as the badge sitting inside it.
 *
 * Written as complete literal class names — Tailwind scans source statically and
 * would not emit a class assembled by interpolation.
 */
export const RFX_STATUS_BORDER_CLASS: Record<RfxStatusColour, string> = {
	active: "border-utility-success-700",
	default: "border-gray-700",
	error: "border-utility-error-700",
	success: "border-utility-blue-light-700",
};

/** Top-border class for a card showing `status`. */
export const getRfxStatusBorderClass = (
	status: string | null | undefined
): string =>
	RFX_STATUS_BORDER_CLASS[getRfxStatusPresentation(status).colour];
